/**
 * Asset Performance Store
 * Integrates SharePoint data with Rule Engine
 */

import { create } from "zustand";
import sharePointService from "../services/sharePointService";
import ruleEngine from "../services/ruleEngine";
import staffPerformanceEngine from "../services/staffPerformanceEngine";
import ytdMetricsEngine from "../services/ytdMetricsEngine";
import projectTimelineEngine from "../services/projectTimelineEngine";
import alertDeduplicationService from "../services/alertDeduplicationService";

const useStore = create((set, get) => ({
  // ==================== USER & AUTH ====================
  user: null,
  accessToken: null,
  userRole: "Viewer", // Default to Viewer until we verify role
  isEvaluating: false,

  setUser: (user) => set({ user }),

  setAccessToken: (token) => {
    sharePointService.setAccessToken(token);
    set({ accessToken: token });
  },

  setUserRole: (role) => set({ userRole: role }),

  // ==================== ASSETS ====================
  assets: [],
  selectedAsset: null,

  fetchAssets: async () => {
    try {
      const assets = await sharePointService.getAssets();
      set({ assets });

      // Auto-select first asset if none selected
      if (!get().selectedAsset && assets.length > 0) {
        await get().selectAsset(assets[0].Id);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  },

  selectAsset: async (assetId) => {
    const asset = get().assets.find((a) => a.Id == assetId);
    if (!asset) return;

    set({
      selectedAsset: asset,
      isLoading: true,
      // Clear ALL state
      entities: [],
      entitiesByType: {},
      signals: [],
      latestSignals: new Map(),
      rules: [],
      alerts: [],
      unresolvedAlerts: [],
      entitiesWithYTD: [],
      metricsSummary: null,
      evaluation: null,
      targetMetrics: [],
      achievableMetrics: [],
      blockedAnalysis: [],
      dependencyGraph: null,
      explanations: null,
      staffPerformance: [],
      performanceSummary: null,
    });

    await get().loadAssetData(assetId);

    set({ isLoading: false });
  },

  // ==================== ENTITIES ====================
  entities: [],
  entitiesByType: {},

  fetchEntities: async (assetId) => {
    try {
      const entities = await sharePointService.getEntitiesForAsset(assetId);

      // Group by type
      const byType = entities.reduce((acc, entity) => {
        const type = entity.EntityType;
        if (!acc[type]) acc[type] = [];
        acc[type].push(entity);
        return acc;
      }, {});

      set({
        entities,
        entitiesByType: byType,
      });
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  },

  createEntity: async (entityData) => {
    try {
      const assetId = get().selectedAsset?.Id;
      if (!assetId) throw new Error("No asset selected");

      await sharePointService.createEntity({
        ...entityData,
        AssetIdLookupId: assetId,
      });

      await get().fetchEntities(assetId);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error creating entity:", error);
      throw error;
    }
  },

  updateEntity: async (entityId, entityData) => {
    try {
      await sharePointService.updateEntity(entityId, entityData);
      await get().fetchEntities(get().selectedAsset?.Id);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error updating entity:", error);
      throw error;
    }
  },

  deleteEntity: async (entityId) => {
    try {
      await sharePointService.deleteEntity(entityId);
      await get().fetchEntities(get().selectedAsset?.Id);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error deleting entity:", error);
      throw error;
    }
  },

  // ==================== SIGNALS ====================
  signals: [],
  latestSignals: new Map(),

  fetchSignals: async (assetId) => {
    try {
      const signals = await sharePointService.getSignalsForAsset(assetId);

      // Build map of latest signals per entity
      const latestMap = new Map();
      const entities = get().entities;

      entities.forEach((entity) => {
        const entitySignals = signals
          .filter((s) => s.EntityIdLookupId == entity.Id)
          .sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));

        if (entitySignals.length > 0) {
          latestMap.set(entity.Id, entitySignals[0]);
        }
      });

      set({
        signals,
        latestSignals: latestMap,
      });
    } catch (error) {
      console.error("Error fetching signals:", error);
    }
  },

  createSignal: async (signalData) => {
    try {
      // Now supports both create and update
      await sharePointService.createSignal(signalData);
      await get().fetchSignals(get().selectedAsset?.Id);
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Evaluate asset to recalculate YTD
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error creating/updating signal:", error);
      throw error;
    }
  },
  // ==================== DEPENDENCY RULES ====================
  rules: [],

  fetchRules: async (assetId) => {
    try {
      const rules = await sharePointService.getRulesForAsset(assetId);
      set({ rules });
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  },

  createRule: async (ruleData) => {
    try {
      await sharePointService.createRule(ruleData);
      await get().fetchRules(get().selectedAsset?.Id);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error creating rule:", error);
      throw error;
    }
  },

  updateRule: async (ruleId, ruleData) => {
    try {
      await sharePointService.updateRule(ruleId, ruleData);
      await get().fetchRules(get().selectedAsset?.Id);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error updating rule:", error);
      throw error;
    }
  },

  deleteRule: async (ruleId) => {
    try {
      await sharePointService.deleteRule(ruleId);
      await get().fetchRules(get().selectedAsset?.Id);
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error deleting rule:", error);
      throw error;
    }
  },

  // ==================== ALERTS ====================
  alerts: [],
  unresolvedAlerts: [],

  fetchAlerts: async (assetId) => {
    try {
      const [all, unresolved] = await Promise.all([
        sharePointService.getAlerts(assetId, false),
        sharePointService.getAlerts(assetId, true),
      ]);

      set({
        alerts: all,
        unresolvedAlerts: unresolved,
      });
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  },

  createAlert: async (alertData) => {
    try {
      await sharePointService.createAlert({
        ...alertData,
        AssetIdLookupId: get().selectedAsset?.Id,
      });
      // await get().fetchAlerts(get().selectedAsset?.Id);
    } catch (error) {
      console.error("Error creating alert:", error);
      throw error;
    }
  },

  resolveAlert: async (alertId) => {
    try {
      await sharePointService.resolveAlert(alertId);
      await get().fetchAlerts(get().selectedAsset?.Id);
    } catch (error) {
      console.error("Error resolving alert:", error);
      throw error;
    }
  },

  // ==================== RULE ENGINE EVALUATION ====================
  evaluation: null,
  targetMetrics: [],
  achievableMetrics: [],
  blockedAnalysis: [],
  dependencyGraph: null,
  explanations: null,

  // ==================== STAFF PERFORMANCE ====================
  staffPerformance: [],
  performanceSummary: null,

  // ==================== YTD METRICS ====================
  entitiesWithYTD: [],
  metricsSummary: null,

  evaluateAsset: async () => {
    try {
      if (get().isEvaluating) {
        console.log("[Evaluation] Already in progress, skipping...");
        return;
      }

      set({ isEvaluating: true });

      const assetId = get().selectedAsset?.Id;
      if (!assetId) {
        set({ isEvaluating: false });
        return;
      }

      const { entities, signals, rules, latestSignals, alerts } = get();

      const entitiesWithDeadlines =
        projectTimelineEngine.enhanceEntitiesWithDeadlineStatus(entities);

      const entitiesWithYTD = ytdMetricsEngine.calculateYTDMetrics(
        entitiesWithDeadlines,
        signals,
      );

      const metricsSummary =
        ytdMetricsEngine.getMetricsSummary(entitiesWithYTD);

      const result = await ruleEngine.evaluateAsset(
        assetId,
        entitiesWithYTD,
        Array.from(signals),
        rules,
      );

      // NEW: Merge adjusted targets into entities
      const entitiesWithAdjustedTargets = entitiesWithYTD.map((entity) => {
        const adjustedTargetInfo = result.targetMetrics.find(
          (tm) => tm.entityId === entity.Id,
        );

        if (adjustedTargetInfo) {
          return {
            ...entity,
            baseTargetValue: adjustedTargetInfo.baseTargetValue,
            adjustedTargetValue: adjustedTargetInfo.targetValue,
            targetContributions: adjustedTargetInfo.contributions || [],
            hasAdjustedTarget:
              adjustedTargetInfo.targetValue !==
              adjustedTargetInfo.baseTargetValue,
          };
        }

        return entity;
      });

      const staffPerformanceData =
        staffPerformanceEngine.calculateStaffPerformance(
          entitiesWithAdjustedTargets,
          latestSignals,
        );

      const performanceSummaryData =
        staffPerformanceEngine.getPerformanceSummary(staffPerformanceData);

      set({
        entitiesWithYTD: entitiesWithAdjustedTargets, // Use enhanced entities
        metricsSummary,
        evaluation: result,
        targetMetrics: result.targetMetrics,
        achievableMetrics: result.achievableMetrics,
        blockedAnalysis: result.blockedAnalysis,
        dependencyGraph: result.dependencyGraph,
        explanations: result.explanations,
        staffPerformance: staffPerformanceData,
        performanceSummary: performanceSummaryData,
      });

      await alertDeduplicationService.autoResolveFixedAlerts(
        alerts,
        entitiesWithAdjustedTargets,
        get().resolveAlert,
      );

      await get().generateAlertsFromEvaluation(
        result,
        entitiesWithAdjustedTargets,
      );
    } catch (error) {
      console.error("Error evaluating asset:", error);
    } finally {
      set({ isEvaluating: false });
    }
  },

  generateAlertsFromEvaluation: async (evaluation, entitiesWithYTD) => {
    const { selectedAsset, alerts, createAlert } = get();
    if (!selectedAsset) return;

    const alertsToCreate = [];

    // 1. Generate alerts from dependency failures (NEW - Priority)
    if (evaluation.dependencyAlerts && evaluation.dependencyAlerts.length > 0) {
      evaluation.dependencyAlerts.forEach((depAlert) => {
        alertsToCreate.push({
          AssetIdLookupId: selectedAsset.Id,
          EntityIdLookupId: depAlert.entityId,
          Severity: depAlert.severity,
          Message: depAlert.message,
          Explanation: depAlert.explanation,
          AlertType: depAlert.alertType,
        });
      });
    }

    // 2. Generate alerts from blocked analysis (existing)
    if (evaluation.blockedAnalysis) {
      evaluation.blockedAnalysis.forEach((analysis) => {
        if (analysis.blocked > 0 && analysis.percentBlocked > 10) {
          alertsToCreate.push({
            AssetIdLookupId: selectedAsset.Id,
            EntityIdLookupId: analysis.entityId,
            Severity: analysis.percentBlocked > 50 ? "Critical" : "Warning",
            Message: `${analysis.entityTitle}: ${analysis.blocked.toFixed(0)} blocked`,
            Explanation: `Target: ${analysis.target}, Achievable: ${analysis.achievable.toFixed(0)}. Root causes: ${analysis.rootCauses.map((rc) => rc.source).join(", ")}`,
            AlertType: "Performance",
          });
        }
      });
    }

    // 3. Batch create with deduplication (existing)
    if (alertsToCreate.length > 0) {
      const alertDeduplicationService = (
        await import("../services/alertDeduplicationService")
      ).default;
      await alertDeduplicationService.batchCreateWithDeduplication(
        alertsToCreate,
        alerts,
        createAlert,
      );
    }
  },

  // ==================== COMPOSITE OPERATIONS ====================

  loadAssetData: async (assetId) => {
    try {
      // Fetch all data in parallel
      await Promise.all([
        get().fetchEntities(assetId),
        get().fetchRules(assetId),
        get().fetchAlerts(assetId),
      ]);

      // Fetch signals after entities (depends on entities)
      await get().fetchSignals(assetId);

      // Run evaluation
      await get().evaluateAsset();
    } catch (error) {
      console.error("Error loading asset data:", error);
    }
  },

  refreshAllData: async () => {
    const assetId = get().selectedAsset?.Id;
    if (!assetId) return;

    set({ isLoading: true });
    await get().loadAssetData(assetId);
    set({ isLoading: false });
  },

  // ==================== ALL-ASSETS OVERVIEW ====================
  allAssetsOverview: [],
  isFetchingOverview: false,

  /**
   * Fetches entities, signals, and rules for ALL assets in parallel,
   * runs the rule engine for each, and stores the results.
   * Used exclusively by the LandingPage overview.
   */
  fetchAllAssetsOverview: async () => {
    const { assets } = get();
    if (!assets.length || get().isFetchingOverview) return;

    set({ isFetchingOverview: true });

    try {
      const results = await Promise.all(
        assets.map(async (asset) => {
          try {
            const [entities, signals, rules] = await Promise.all([
              sharePointService.getEntitiesForAsset(asset.Id),
              sharePointService.getSignalsForAsset(asset.Id),
              sharePointService.getRulesForAsset(asset.Id),
            ]);

            const entitiesWithDeadlines =
              projectTimelineEngine.enhanceEntitiesWithDeadlineStatus(entities);
            const entitiesWithYTD = ytdMetricsEngine.calculateYTDMetrics(
              entitiesWithDeadlines,
              signals,
            );

            // Build latestSignals map (entity.Id → latest signal)
            const latestSignals = new Map();
            entities.forEach((entity) => {
              const sorted = signals
                .filter((s) => s.EntityIdLookupId == entity.Id)
                .sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));
              if (sorted.length > 0) latestSignals.set(entity.Id, sorted[0]);
            });

            const evaluation = await ruleEngine.evaluateAsset(
              asset.Id,
              entitiesWithYTD,
              signals,
              rules,
            );

            return {
              asset,
              entities: entitiesWithYTD,
              ruleEvaluations: evaluation.ruleEvaluations || [],
              latestSignals,
            };
          } catch (err) {
            console.error(`[Overview] Failed to load asset ${asset.Title}:`, err);
            return {
              asset,
              entities: [],
              ruleEvaluations: [],
              latestSignals: new Map(),
            };
          }
        }),
      );

      set({ allAssetsOverview: results });
    } catch (error) {
      console.error('[Overview] Failed to fetch all assets overview:', error);
    } finally {
      set({ isFetchingOverview: false });
    }
  },

  // ==================== GLOBAL STATE ====================
  isLoading: false,
  error: null,

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  // ==================== INITIALIZATION ====================

  initialize: async (siteUrl) => {
    try {
      // Graph API service gets site URL from authConfig automatically
      await get().fetchAssets();
    } catch (error) {
      console.error("Error initializing store:", error);
      set({ error: error.message });
    }
  },
}));

export default useStore;
